import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { useToast } from "hooks/useToast";
import { perkFormSchema } from "formValidations/perkSchema";
import { FormikForm } from "components/FormikForm/FormikForm";
import { AdminPerk, useAdminUpdatePerkMutation } from "store/apis/perksCrm";
import { BackendError } from "types/utils/error";
import { buildUpdatePerkFormData } from "util/perks/buildPerkFormData";
import { getPerkFormValuesFromPerk } from "util/perks/perkFormValues";
import { PerkForm } from "./PerkForm";
import { PerkFormValues } from "./perksCrm.types";
import { PerkType } from "constants/perksAdmin";

type EditPerkModalProps = {
  perk: AdminPerk;
  onClose: () => void;
};

export const EditPerkModal = ({ perk, onClose }: EditPerkModalProps) => {
  const { successToast, alertToast } = useToast();
  const [adminUpdatePerk, { isLoading }] = useAdminUpdatePerkMutation();

  const handleSubmit = async (values: PerkFormValues) => {
    try {
      await adminUpdatePerk({
        perkId: perk.perk_id,
        formData: buildUpdatePerkFormData(values),
      }).unwrap();
      successToast({ message: "Perk updated successfully" });
      onClose();
    } catch (err) {
      alertToast({
        message:
          (err as BackendError)?.data?.error?.message ||
          "Failed to update perk",
      });
    }
  };

  return (
    <Modal.Root open onOpenChange={onClose}>
      <Modal.Content size="xl" useCustomOverlay>
        <Modal.Header>
          <Modal.Title>Edit Perk — {perk.name}</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <FormikForm
          initialValues={getPerkFormValuesFromPerk(perk)}
          validationSchema={perkFormSchema}
          onSubmit={handleSubmit}
        >
          <Modal.Body>
            <PerkForm
              existingIconUrl={perk.icon}
              perkType={perk.perk_type as PerkType}
            />
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Modal.RawClose asChild>
              <Button type="button" customType="secondary">
                Cancel
              </Button>
            </Modal.RawClose>
            <Button type="submit" customType="primary" isLoading={isLoading}>
              Save Changes
            </Button>
          </Modal.FooterButtonGroup>
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
